import type { CreateInquiryRequest } from '../types';
import { isValidApartmentId } from '../data/apartments';

export interface ValidationErrors {
  [field: string]: string;
}

const NAME_PATTERN = /^[\p{L}\s\-']+$/u;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[\d\s+\-()]*$/; // Allow empty for optional field
const MIN_DATE_NOTICE = 1; // Days advance notice required

export function validateInquiry(data: Partial<CreateInquiryRequest>): ValidationErrors {
  const errors: ValidationErrors = {};

  // Name validation
  if (!data.guestName || data.guestName.trim().length === 0) {
    errors.guestName = 'validation.nameRequired';
  } else if (data.guestName.trim().length < 2) {
    errors.guestName = 'validation.nameMinLength';
  } else if (data.guestName.trim().length > 200) {
    errors.guestName = 'validation.nameMaxLength';
  } else if (!NAME_PATTERN.test(data.guestName.trim())) {
    errors.guestName = 'validation.nameRequired';
  }

  // Email validation
  if (!data.guestEmail || data.guestEmail.trim().length === 0) {
    errors.guestEmail = 'validation.emailRequired';
  } else if (!EMAIL_PATTERN.test(data.guestEmail.trim())) {
    errors.guestEmail = 'validation.emailInvalid';
  }

  // Phone validation (optional)
  if (data.guestPhone && data.guestPhone.trim().length > 0) {
    if (!PHONE_PATTERN.test(data.guestPhone.trim())) {
      errors.guestPhone = 'validation.phoneInvalid';
    }
  }

  // Check-in date validation
  if (!data.checkInDate) {
    errors.checkInDate = 'validation.checkInRequired';
  } else {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkIn = new Date(data.checkInDate);
    if (checkIn < today) {
      errors.checkInDate = 'validation.checkInPast';
    } else {
      // Optionally require advance notice
      const daysAhead = Math.floor((checkIn.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (daysAhead < MIN_DATE_NOTICE) {
        errors.checkInDate = 'validation.checkInAdvanceNotice';
      }
    }
  }

  // Check-out date validation
  if (!data.checkOutDate) {
    errors.checkOutDate = 'validation.checkOutRequired';
  } else if (data.checkInDate && data.checkOutDate) {
    const checkIn = new Date(data.checkInDate);
    const checkOut = new Date(data.checkOutDate);
    checkOut.setHours(0, 0, 0, 0);
    checkIn.setHours(0, 0, 0, 0);
    if (checkOut <= checkIn) {
      errors.checkOutDate = 'validation.checkOutBeforeCheckIn';
    }
  }

  // Apartment validation
  if (!data.apartmentId || data.apartmentId <= 0) {
    errors.apartmentId = 'validation.apartmentRequired';
  } else if (!isValidApartmentId(data.apartmentId)) {
    errors.apartmentId = 'validation.apartmentNotFound';
  }

  // Adults validation
  if (data.numAdults === undefined || data.numAdults === null || data.numAdults < 1) {
    errors.numAdults = 'validation.adultsRequired';
  } else if (data.numAdults > 20) {
    errors.numAdults = 'validation.adultsTooMany';
  }

  // Children validation
  if (data.numChildren !== undefined && data.numChildren !== null) {
    if (data.numChildren < 0) {
      errors.numChildren = 'validation.childrenInvalid';
    } else if (data.numChildren > 20) {
      errors.numChildren = 'validation.childrenTooMany';
    }
  }

  // Message validation
  if (data.message) {
    const trimmedMsg = data.message.trim();
    if (trimmedMsg.length > 500) {
      errors.message = 'validation.messageMaxLength';
    }
  }

  // Honeypot validation (spam detection)
  if (data.honeypot && data.honeypot.trim().length > 0) {
    errors.honeypot = 'validation.spamDetected';
  }

  return errors;
}

export function hasErrors(errors: ValidationErrors): boolean {
  return Object.keys(errors).length > 0;
}
